import React, { useCallback } from 'react';
import { ToolApprovalRequestContent } from '../../../../libs/models/agui/FunctionCallContent';
import { AIContent } from '../../../../libs/models/conversations/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { AIModelName } from '../../../../libs/models/app/AIModel';
import { MailApprovalCard } from './MailApprovalCard';
import { GenericApprovalCard } from './GenericApprovalCard';
import { ApprovalActions, SessionApprovalMode } from './ApprovalActions';

interface Props {
  content: ToolApprovalRequestContent;
  /** 같은 메시지의 형제 콘텐츠 (응답 여부 판단용) */
  contents?: AIContent[];
  isLast?: boolean;
}

/** 승인 요청(toolApprovalRequest) 을 도구 이름별로 분기 */
export const ApprovalRenderer: React.FC<Props> = ({ content, contents, isLast }) => {
  const dispatch = useAppDispatch();
  const { selectedId, conversations } = useAppSelector((s: RootState) => s.conversations_v2);
  const { selectedModel } = useAppSelector((s: RootState) => s.models);

  const { name, arguments: args, callId } = content.toolCall;
  const requestId = content.requestId ?? callId;
  
  const findApproved = useCallback((): boolean | null => {
    const msgs = conversations[selectedId]?.messages?.data ?? [];
    for (const m of msgs) {
      const res = m.contents?.find((c) => {
        if ((c as { $type?: string }).$type !== 'toolApprovalResponse') return false;
        const r = c as { requestId?: string; toolCall?: { callId?: string } };
        return r.requestId === requestId || r.toolCall?.callId === callId;
      }) as { approved?: boolean } | undefined;
      if (res) return res.approved === true;
    }
  
    const sib = (contents ?? []).find(
      (c) =>
        (c as { $type?: string }).$type === 'toolApprovalResponse' &&
        (c as { toolCall?: { callId?: string } }).toolCall?.callId === callId,
    ) as { approved?: boolean } | undefined;
    return sib ? sib.approved === true : null;
  }, [conversations, selectedId, requestId, callId, contents]);

  const approved = findApproved();
  const answered = approved !== null;

  const respond = (ok: boolean, sessionMode?: SessionApprovalMode) => {
    dispatch({
      type: 'conversations/v2/submitFunctionResult',
      payload: {
        sessionId: selectedId,
        model: selectedModel.model as AIModelName,
        callId,
        result: { approval_id: requestId, approved: ok },
        sessionApprovalMode: sessionMode,
      },
    });
  };

 
  const isMail = name === 'OutlookMailConnector_SendEmail' || name === 'SendEmail';
  const footer =
    answered || isLast ? (
      <ApprovalActions
        onRespond={respond}
        answered={answered}
        approvedResult={approved === true}
        showAlways={!isMail}
      />
    ) : null;

  if (isMail) {
    return (
      <MailApprovalCard
        recipients={typeof args.recipients === 'string' ? args.recipients : undefined}
        subject={typeof args.subject === 'string' ? args.subject : undefined}
        content={typeof args.content === 'string' ? args.content : undefined}
        answered={answered}
        footer={footer}
      />
    );
  }

  return (
    <GenericApprovalCard functionName={name} functionArguments={args} footer={footer} />
  );
};
